import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Briefcase, Building2, Download, FileText, GraduationCap, Loader2, Mail, Pencil, Phone, Trash2, UserPlus,
} from 'lucide-react';
import type { CandidateStage } from '@pms/shared';
import { PERMISSIONS } from '@pms/shared';
import { recruitmentService } from '@/services/recruitment.service';
import { usePermission } from '@/hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RightSheet, Avatar, StageBadge } from './shared';
import { STAGE_META, formatDate, fullName, getErrorMessage } from './utils';
import { ConvertCandidateModal } from './ConvertCandidateModal';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

const ALL_STAGES: CandidateStage[] = [
  'new', 'screening', 'interview', 'selected', 'offer_released',
  'offer_accepted', 'offer_rejected', 'rejected', 'on_hold',
];

export function CandidateDrawer({
  candidateId, onClose, onEdit,
}: {
  candidateId: string;
  onClose: () => void;
  onEdit: () => void;
}) {
  const queryClient = useQueryClient();
  const { has } = usePermission();
  const canManage = has(PERMISSIONS.RECRUITMENT_MANAGE);
  const canConvert = canManage && has(PERMISSIONS.STAFF_CREATE);

  const [stage, setStage] = useState<CandidateStage | ''>('');
  const [comment, setComment] = useState('');
  const [convertOpen, setConvertOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const q = useQuery({
    queryKey: ['recruitment', 'candidate', candidateId],
    queryFn: () => recruitmentService.getCandidate(candidateId),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['recruitment'] });
  };

  const moveStage = useMutation({
    mutationFn: () => recruitmentService.moveStage(candidateId, {
      stage: stage as CandidateStage,
      comment: comment || null,
      rejection_reason: (stage === 'rejected' || stage === 'offer_rejected') ? comment || null : null,
      on_hold_reason: stage === 'on_hold' ? comment || null : null,
    }),
    onSuccess: () => { toast.success('Stage updated'); setStage(''); setComment(''); invalidate(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: () => recruitmentService.deleteCandidate(candidateId),
    onSuccess: () => { toast.success('Candidate deleted'); invalidate(); onClose(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const viewResume = async () => {
    try {
      const blob = await recruitmentService.downloadResume(candidateId);
      window.open(URL.createObjectURL(blob), '_blank', 'noopener');
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const c = q.data;

  return (
    <>
      <RightSheet
        open
        onOpenChange={(o) => { if (!o) onClose(); }}
        title={c ? fullName(c.first_name, c.last_name) : 'Candidate'}
        subtitle={c?.candidate_code}
      >
        {q.isLoading || !c ? (
          <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <Avatar first={c.first_name} last={c.last_name} size="lg" />
              <div className="min-w-0">
                <StageBadge stage={c.stage} />
                <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground"><Mail className="h-3.5 w-3.5" />{c.email}</p>
                <p className="flex items-center gap-1.5 text-sm text-muted-foreground"><Phone className="h-3.5 w-3.5" />{c.phone}</p>
              </div>
            </div>

            {(c.has_resume || canConvert) && (
              <div className="flex flex-wrap gap-2">
                {c.has_resume && (
                  <Button size="sm" variant="outline" onClick={viewResume}><Download className="h-3.5 w-3.5" /> View Resume</Button>
                )}
                {canConvert && c.stage !== 'converted' && (
                  <Button size="sm" onClick={() => setConvertOpen(true)}><UserPlus className="h-3.5 w-3.5" /> Convert to Employee</Button>
                )}
              </div>
            )}

            <Section title="Application">
              <Row label="Department" value={c.applied_department_name} icon={Building2} />
              <Row label="Designation" value={c.applied_designation_name} icon={Briefcase} />
              <Row label="Source" value={c.source_details ? `${c.source} · ${c.source_details}` : c.source} />
              <Row label="Notice Period" value={c.notice_period_days != null ? `${c.notice_period_days} days` : null} />
              <Row label="Current / Expected CTC" value={[c.current_ctc, c.expected_ctc].some(Boolean) ? `₹${c.current_ctc ?? '—'} / ₹${c.expected_ctc ?? '—'}` : null} />
            </Section>

            {c.education.length > 0 && (
              <Section title="Education">
                {c.education.map((e) => (
                  <div key={e.id} className="flex items-start gap-2 text-sm">
                    <GraduationCap className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="text-foreground">{e.degree}{e.institute ? `, ${e.institute}` : ''} {e.year ? `(${e.year})` : ''} {e.percentage != null ? `· ${e.percentage}%` : ''}</span>
                  </div>
                ))}
              </Section>
            )}

            {c.experience.length > 0 && (
              <Section title="Experience">
                {c.experience.map((x) => (
                  <div key={x.id} className="flex items-start gap-2 text-sm">
                    <Briefcase className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="text-foreground">{x.designation ?? '—'} @ {x.company_name} {x.is_current ? '(current)' : ''}</span>
                  </div>
                ))}
              </Section>
            )}

            {canManage && c.stage !== 'converted' && (
              <Section title="Move Stage">
                <div className="space-y-2">
                  <Select value={stage} onValueChange={(v) => setStage(v as CandidateStage)}>
                    <SelectTrigger><SelectValue placeholder="Select new stage…" /></SelectTrigger>
                    <SelectContent>
                      {ALL_STAGES.filter((s) => s !== c.stage).map((s) => <SelectItem key={s} value={s}>{STAGE_META[s].label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Textarea rows={2} placeholder="Comment / reason (optional)" value={comment} onChange={(e) => setComment(e.target.value)} />
                  <Button size="sm" disabled={!stage || moveStage.isPending} onClick={() => moveStage.mutate()}>
                    {moveStage.isPending ? 'Updating…' : 'Update Stage'}
                  </Button>
                </div>
              </Section>
            )}

            {c.logs.length > 0 && (
              <Section title="Activity">
                <div className="space-y-2">
                  {c.logs.map((l) => (
                    <div key={l.id} className="flex items-start gap-2 text-xs text-muted-foreground">
                      <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>
                        {l.action.replace(/_/g, ' ')}
                        {l.new_stage ? ` → ${STAGE_META[l.new_stage].label}` : ''}
                        {l.comment ? ` · ${l.comment}` : ''}
                        <span className="ml-1 opacity-70">{formatDate(l.performed_at)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {canManage && (
              <div className="flex gap-2 border-t border-border/60 pt-4">
                <Button variant="outline" className="flex-1" onClick={onEdit}><Pencil className="h-4 w-4" /> Edit</Button>
                <Button
                  variant="outline"
                  className="flex-1 border-destructive/40 text-destructive hover:bg-destructive/10"
                  disabled={remove.isPending}
                  onClick={() => setDeleteConfirm(true)}
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </Button>
              </div>
            )}
          </div>
        )}
      </RightSheet>

      {convertOpen && c && (
        <ConvertCandidateModal
          candidate={c}
          onClose={() => setConvertOpen(false)}
          onConverted={() => { setConvertOpen(false); invalidate(); onClose(); }}
        />
      )}

      <ConfirmDialog
        open={deleteConfirm}
        onOpenChange={setDeleteConfirm}
        title="Delete this candidate?"
        description="This candidate and all their data will be permanently deleted."
        confirmLabel="Delete"
        onConfirm={() => remove.mutate()}
      />
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ label, value, icon: Icon }: { label: string; value?: string | null; icon?: typeof Mail }) {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="flex items-center gap-1.5 text-muted-foreground">{Icon && <Icon className="h-3.5 w-3.5" />}{label}</span>
      <span className="text-right font-medium text-foreground">{value}</span>
    </div>
  );
}
